import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  AssetRoutine,
  AuditLog,
  IssueMessageType,
  Message,
  RoutineIdpStatus,
  WorkflowPageConfig,
  WorkflowQuestionConfig,
  WorkflowResponseState,
} from '@gripp/shared-logic';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../../../themes/colors';
import { useIssueContext } from '../../../issue/issueContext';
import { WorkflowMessageQuestion } from './workflowMessageQuestion';
import { themedStyleSheet } from '../../../../themes/themedStyleSheet';

type WorkflowMessagePageProps = {
  page: WorkflowPageConfig;
  responses: WorkflowResponseState[];
  exceptionsOnly?: boolean;
  assetRoutine: AssetRoutine;
  message: Message;
  auditLog?: AuditLog[];
  /** Routine-level IDP toggle, forwarded to each question. */
  idpEnabled?: boolean;
  /** Live extraction status, forwarded to each question. */
  idpStatus?: RoutineIdpStatus;
};

interface QuestionResponse {
  index: number;
  config: WorkflowQuestionConfig;
  response: WorkflowResponseState;
}

export const WorkflowMessagePage = (props: WorkflowMessagePageProps) => {
  const { t } = useTranslation();
  const { onRouteToIssue } = useIssueContext();

  // Derived from props on every render, not snapshotted into state by a
  // mount-only effect as it used to be (plan 0027 finding #8). The old
  // `useEffect([], ...)` froze this list at whatever the first render saw, so
  // an IDP extraction landing while the Activity was open could not reveal its
  // values — the fresh responses arrived as new props and were ignored until
  // the reviewer reloaded the page. Nothing wanted the freeze; it was a
  // roundabout way of computing derived data.
  const responses = useMemo<QuestionResponse[]>(() => {
    const ordered = [...props.page.questions]
      .sort((a, b) => a.order - b.order)
      .map((question, index) => {
        const response = props.responses.find(
          (response) => response.questionId === question.id
        );

        return { index: index + 1, config: question, response };
      })
      .filter((item): item is QuestionResponse => item.response !== undefined);

    return props.exceptionsOnly
      ? ordered.filter(
          (x) =>
            x.response.exceptionCount !== undefined &&
            x.response.exceptionCount > 0
        )
      : ordered;
  }, [props.page.questions, props.responses, props.exceptionsOnly]);

  const exceptionCount = props.assetRoutine.exceptionCount ?? 0;
  const linkButtonTextKey =
    props.message.type === IssueMessageType.RoutineExceptionMessage
      ? 'routines.exceptions.viewRelatedRoutine'
      : 'routines.exceptions.viewRelatedReport';

  return (
    <>
      {exceptionCount > 0 && (
        <Text style={styles.exceptionCount}>
          {t('routines.exceptions.exceptionsNoticed', {
            count: props.assetRoutine.exceptionCount,
          })}
        </Text>
      )}
      {responses.map((response) => (
        <WorkflowMessageQuestion
          key={response.index}
          index={response.index}
          question={response.config}
          response={response.response}
          auditLog={props.auditLog}
          idpEnabled={props.idpEnabled}
          idpStatus={props.idpStatus}
        />
      ))}
      {exceptionCount > 0 && props.message.linkedIssue && !props.auditLog && (
        <>
          {props.message.type === IssueMessageType.RoutineMessage && (
            <Text style={styles.additionalText}>
              {t('routines.exceptions.reportCreated')}
            </Text>
          )}
          <Pressable
            onPress={() => onRouteToIssue(props.message.linkedIssue?.id)}
            style={styles.linkButton}
          >
            <Text style={styles.linkButtonText}>{t(linkButtonTextKey)}</Text>
            <MaterialCommunityIcons
              name="arrow-right"
              size={24}
              color={Colors.primary}
            />
          </Pressable>
          {props.message.type === IssueMessageType.RoutineExceptionMessage && (
            <Text style={styles.additionalText}>
              {t('routines.exceptions.addDetails')}
            </Text>
          )}
        </>
      )}
    </>
  );
};

const styles = themedStyleSheet((role) => ({
  exceptionCount: {
    marginVertical: 8,
    color: Colors.redText,
  },
  linkButton: {
    marginTop: 16,
    borderWidth: 2,
    borderColor: Colors.primary,
    borderRadius: 8,
    padding: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  linkButtonText: {
    color: Colors.primary,
    marginRight: 8,
    fontWeight: '500',
  },
  additionalText: {
    marginTop: 8,
    color: role.textStrong,
  },
}));
